import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import type { Profile } from '../types/database';

export function useProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let mounted = true;

    if (!user) {
      if (mounted) {
        setProfile(null);
        setLoading(false);
      }
      return;
    }

    async function fetchProfile() {
      try {
        console.log('useProfile: Fetching profile for user:', user.id);
        const { data, error: queryError } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        if (queryError) throw queryError;
        
        if (mounted) {
          console.log('useProfile: Initial profile data:', data);
          setProfile(data);
        }
      } catch (err) {
        console.error('useProfile: Error fetching profile:', err);
        if (mounted) {
          setError(err instanceof Error ? err : new Error('Failed to fetch profile'));
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    fetchProfile();

    // Set up real-time subscription to profile changes
    const channelName = `profile-changes-${user.id}`;
    console.log(`useProfile: Setting up subscription on channel ${channelName}`);
    
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles',
          filter: `user_id=eq.${user.id}`,
        },
        async (payload) => {
          console.log('useProfile: Profile change detected:', payload);
          
          if (!mounted) return;

          try {
            if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
              // Fetch the complete updated profile
              const { data: updatedProfile, error } = await supabase
                .from('profiles')
                .select('*')
                .eq('user_id', user.id)
                .single();

              if (error) {
                console.error('useProfile: Error fetching updated profile:', error);
                return;
              }

              console.log('useProfile: Setting updated profile:', updatedProfile);
              setProfile(updatedProfile);
            } else if (payload.eventType === 'DELETE') {
              setProfile(null);
            }
          } catch (error) {
            console.error('useProfile: Error in subscription handler:', error);
          }
        }
      )
      .subscribe((status) => {
        console.log(`useProfile: Subscription status for ${channelName}:`, status);
      });

    return () => {
      console.log(`useProfile: Cleaning up subscription for ${channelName}`);
      mounted = false;
      supabase.removeChannel(channel);
    };
  }, [user]);

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) throw new Error('Must be logged in to update profile');

    try {
      console.log('updateProfile: Updating with:', updates);
      
      // Always include updated_at to ensure the change is detected
      const updatedData = {
        ...updates,
        updated_at: new Date().toISOString(),
      };
      
      const { data, error } = await supabase
        .from('profiles')
        .update(updatedData)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        console.error('updateProfile error:', error);
        throw error;
      }
      
      console.log('updateProfile: Success, updated profile:', data);
      setProfile(data);
      return data;
    } catch (err) {
      console.error('updateProfile: Failed:', err);
      throw err instanceof Error ? err : new Error('Failed to update profile');
    }
  };

  const refreshProfile = async () => {
    if (!user) return;

    try {
      console.log('refreshProfile: Fetching latest profile data');
      setLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) {
        console.error('refreshProfile error:', error);
        throw error;
      }
      
      console.log('refreshProfile: Success, got profile:', data);
      setProfile(data);
      return data;
    } catch (err) {
      console.error('refreshProfile: Failed:', err);
      setError(err instanceof Error ? err : new Error('Failed to refresh profile'));
    } finally {
      setLoading(false);
    }
  };

  return { profile, loading, error, updateProfile, refreshProfile };
}